import React from "react";
import { useTranslation } from "react-i18next";
import { PageHero } from "@/components/site/PageHero";
import { CTASection } from "@/components/site/CTASection";
import { MedicalDisclaimer } from "@/components/site/MedicalDisclaimer";
import { Reveal } from "@/components/motion/Reveal";
import { RefreshCw } from "lucide-react";

export default function Philosophy() {
  const { t } = useTranslation();
  const questions = t("philosophy.coreQuestions", { returnObjects: true }) || [];
  const levels = t("philosophy.levels", { returnObjects: true }) || [];
  const founderNote = t("philosophy.founderNote", { returnObjects: true }) || [];

  return (
    <div data-testid="philosophy-page">
      <PageHero kicker={t("philosophy.kicker")} title={t("philosophy.title")} text={t("philosophy.intro")} />

      {founderNote.length > 0 && (
        <section className="bg-warm-ivory" data-testid="philosophy-founder-note">
          <div className="mx-auto max-w-3xl px-5 py-16 lg:px-0 lg:py-20">
            <Reveal>
              <h2 className="font-serif text-3xl leading-snug text-deep-ink lg:text-4xl">
                {t("philosophy.founderNoteTitle")}
              </h2>
            </Reveal>
            <div className="mt-6 space-y-5">
              {founderNote.map((p, i) => (
                <Reveal key={i} delay={0.04 * i}>
                  <p className="text-lg leading-relaxed text-deep-ink/80">{p}</p>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.06 * founderNote.length}>
              <p className="mt-6 font-serif text-lg italic text-deep-ink/60">
                — {t("doctor.name")}
              </p>
            </Reveal>
          </div>
        </section>
      )}

      <section className="bg-deep-ink text-warm-ivory">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28">
          <Reveal>
            <h2 className="max-w-2xl font-serif text-3xl leading-tight lg:text-4xl">
              {t("philosophy.coreQuestionsTitle")}
            </h2>
          </Reveal>
          <ol className="mt-12 space-y-0">
            {questions.map((q, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <li className="flex gap-6 border-t border-warm-ivory/15 py-6">
                  <span className="font-serif text-2xl text-champagne-gold">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-lg leading-relaxed text-warm-ivory/85">{q}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-warm-ivory">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28">
          <Reveal>
            <h2 className="font-serif text-3xl text-deep-ink lg:text-4xl">{t("philosophy.levelsTitle")}</h2>
          </Reveal>
          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {levels.map((l, i) => (
              <Reveal key={i} delay={(i % 3) * 0.06}>
                <div className="flex h-full flex-col rounded-3xl bg-sand p-7">
                  <span className="font-serif text-3xl text-champagne-gold">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-serif text-2xl text-deep-ink">{l.title}</h3>
                  <p className="mt-2 text-base leading-relaxed text-deep-ink/70">{l.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-sand">
        <div className="mx-auto grid max-w-7xl gap-8 px-5 py-20 lg:grid-cols-2 lg:px-10">
          <Reveal>
            <div>
              <h2 className="font-serif text-3xl text-deep-ink lg:text-4xl">{t("philosophy.treatmentLogicTitle")}</h2>
              <p className="mt-4 text-lg leading-relaxed text-deep-ink/80">{t("philosophy.treatmentLogicText")}</p>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="rounded-3xl bg-warm-ivory p-8 lg:p-10">
              <RefreshCw className="h-6 w-6 text-champagne-gold" strokeWidth={1.6} />
              <h3 className="mt-4 font-serif text-2xl text-deep-ink">{t("philosophy.reassessmentTitle")}</h3>
              <p className="mt-3 text-base leading-relaxed text-deep-ink/70">{t("philosophy.reassessmentText")}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-warm-ivory">
        <div className="mx-auto max-w-3xl px-5 py-12 lg:px-0">
          <MedicalDisclaimer />
        </div>
      </section>

      <CTASection />
    </div>
  );
}
